import {
  IconStackBack,
  IconStackFront,
  IconStackMiddle,
  IconWorld,
  type IconProps,
} from "@tabler/icons-react";

import type { FactScope } from "@/lib/knowledge/definitions";

export const FACT_SCOPE = {
  MACRO: "macro",
  STRATEGIC: "strategic",
  STRUCTURAL: "structural",
  OPERATIONAL: "operational",
} as const satisfies Record<string, FactScope>;

export interface ElevationConfig {
  scope: FactScope;
  label: string;
  framing: string;
  byline: string;
  /** Planning context: what kind of anchor/decision is this? */
  description: string;
  color: string;
  rgb: string;
  icon: (props: IconProps) => React.ReactNode;
}

export const ELEVATIONS: ElevationConfig[] = [
  {
    scope: FACT_SCOPE.MACRO,
    label: "Macro",
    framing: "Environment",
    byline: "External forces",
    description: "External forces shaping the environment",
    color: "gray",
    rgb: "134, 142, 150",
    icon: IconWorld,
  },
  {
    scope: FACT_SCOPE.STRATEGIC,
    label: "Strategic",
    framing: "Intent",
    byline: "What the company intends: direction, priorities, capital bets",
    description: "A direction or bet the company is making",
    color: "blue",
    rgb: "34, 139, 230",
    icon: IconStackFront,
  },
  {
    scope: FACT_SCOPE.STRUCTURAL,
    label: "Structural",
    framing: "Design",
    byline: "How the company is designed to work: roles, teams, incentives",
    description: "How you are organizing or designing to deliver",
    color: "cyan",
    rgb: "21, 170, 191",
    icon: IconStackMiddle,
  },
  {
    scope: FACT_SCOPE.OPERATIONAL,
    label: "Operational",
    framing: "Reality",
    byline: "What actually happens: execution, staffing, delivery",
    description: "What you are actively executing right now",
    color: "teal",
    rgb: "18, 184, 134",
    icon: IconStackBack,
  },
];

export const ELEVATION_BY_SCOPE: Record<FactScope, ElevationConfig> = Object.fromEntries(
  ELEVATIONS.map((e) => [e.scope, e]),
) as Record<FactScope, ElevationConfig>;

/** Scope → Mantine color token. Use for badges, progress bars, etc. */
export const SCOPE_COLOR: Record<string, string> = Object.fromEntries(
  ELEVATIONS.map((e) => [e.scope, e.color]),
);

/** Scope → CSS gradient. Derived from each elevation's color token. */
export const SCOPE_GRADIENT: Record<string, string> = Object.fromEntries(
  ELEVATIONS.map((e) => [
    e.scope,
    `linear-gradient(135deg, var(--mantine-color-${e.color}-5) 0%, var(--mantine-color-${e.color}-7) 100%)`,
  ]),
);

/** Select/dropdown options for scope pickers. */
export const SCOPE_OPTIONS = ELEVATIONS.filter((e) => e.scope !== FACT_SCOPE.MACRO).map((e) => ({
  value: e.scope,
  label: e.label,
}));

/** Elevation display config for signal badges. */
export const ELEVATION_CONFIG: Record<string, { label: string; color: string }> = {
  strategic: { label: "Strategic", color: "blue" },
  structural: { label: "Structural", color: "cyan" },
  operational: { label: "Operational", color: "teal" },
};

/** Capital at risk display config for signal badges. */
export const CAPITAL_CONFIG: Record<string, { label: string; color: string }> = {
  financial: { label: "Financial", color: "gray" },
  human: { label: "Human", color: "gray" },
  intellectual: { label: "Intellectual", color: "gray" },
};

/** Risk type display config for signal badges (Drift/Decay/Drag). */
export const RISK_TYPE_CONFIG: Record<
  string,
  { label: string; color: string; description: string; hint: string }
> = {
  drift: {
    label: "Drift",
    color: "yellow",
    description:
      "Strategy says one thing, reality shows another. The organization is sliding away from its stated intent.",
    hint: "Tension between what leadership intended and what is actually happening.",
  },
  decay: {
    label: "Decay",
    color: "red",
    description:
      "Internal alignment is breaking down. Incentives, systems, or structures are deteriorating within the same level.",
    hint: "Things that should work together are pulling apart.",
  },
  drag: {
    label: "Drag",
    color: "orange",
    description:
      "Execution is heavy. Not misaligned, just slow. Bottlenecks, underutilization, or capability gaps are creating friction.",
    hint: "The work gets done, but harder than it should be.",
  },
};

/** 15-domain taxonomy mirroring backend SIGNAL_DOMAIN_TAXONOMY. */
export const DOMAIN_TAXONOMY: Record<string, string[]> = {
  strategic: [
    "Markets & Expansion",
    "Services & Offerings",
    "Capital Allocation",
    "Strategic Priorities",
    "Leadership & Governance",
  ],
  structural: [
    "Org Design & Roles",
    "Talent & Capacity",
    "Incentives & Performance",
    "Process & Decision Architecture",
    "Systems, Data & Tools",
  ],
  operational: [
    "Sales & Pipeline",
    "Staffing & Resource Allocation",
    "Delivery & Execution",
    "Financial Performance",
    "Continuous Improvement",
  ],
};

/** Reverse lookup: domain name → elevation color. */
export const DOMAIN_COLOR: Record<string, string> = Object.fromEntries(
  Object.entries(DOMAIN_TAXONOMY).flatMap(([elevation, domains]) =>
    domains.map((domain) => [domain, ELEVATION_CONFIG[elevation]?.color ?? "gray"]),
  ),
);

/** Human-readable entity type labels for signal table. */
export const ENTITY_TYPE_LABEL: Record<string, string> = {
  person: "Person",
  organization: "Org",
  team: "Team",
  project: "Project",
  role: "Role",
  skill: "Skill",
  concept: "Concept",
  metric: "Metric",
  location: "Location",
  event: "Event",
};
